/**
 * Bridge to @lumen/shared (Phase C1). The shared package works in bigint masks; the web works in
 * plain numbers (masks ≤ 2^11 so this is lossless). Everything the web needs from the contract
 * package is re-exported here so feature code never imports @lumen/shared directly.
 */
import {
  Permission,
  PERMISSION_BIT_NAMES as SHARED_BIT_NAMES,
  ROLE_PRESETS as SHARED_ROLE_PRESETS,
  has,
  shareLinkMask as sharedShareLinkMask,
  type PermissionBitName,
} from '@lumen/shared';

export type PermissionBit = PermissionBitName;

export const PERMISSION_BIT_NAMES: readonly PermissionBit[] = SHARED_BIT_NAMES;

export const PERMISSION_BITS: Readonly<Record<PermissionBit, number>> = Object.freeze(
  Object.fromEntries(SHARED_BIT_NAMES.map((n) => [n, Number(Permission[n])])) as Record<PermissionBit, number>,
);

export const ROLE_PRESETS: Readonly<Record<keyof typeof SHARED_ROLE_PRESETS, number>> = Object.freeze(
  Object.fromEntries(Object.entries(SHARED_ROLE_PRESETS).map(([k, v]) => [k, Number(v)])) as Record<keyof typeof SHARED_ROLE_PRESETS, number>,
);

export function bitValue(bit: PermissionBit | number): number {
  return typeof bit === 'number' ? bit : PERMISSION_BITS[bit];
}

/** `(mask & bit) === bit` — mask may be a number or an int64 string from JSON. */
export function hasPermission(mask: number | string | bigint | null | undefined, bit: PermissionBit | number): boolean {
  if (mask == null) return false;
  return has(mask, typeof bit === 'number' ? BigInt(bit) : bit);
}

export function maskToBits(mask: number): PermissionBit[] {
  return PERMISSION_BIT_NAMES.filter((b) => hasPermission(mask, b));
}

export function bitsToMask(bits: readonly PermissionBit[]): number {
  return bits.reduce((m, b) => m | PERMISSION_BITS[b], 0);
}

export function shareLinkMask(flags: { allow_download: boolean; allow_comment: boolean }): number {
  return Number(sharedShareLinkMask(flags));
}

export {
  spriteFrameCount,
  SPRITE_TILE_WIDTH,
  SPRITE_COLS,
  partSizeFor,
  partCountFor,
  hashKindFor,
  partBackoffMs,
  shouldRetryPart,
  PART_MAX_ATTEMPTS,
  SAMPLED_HASH_THRESHOLD_BYTES,
  SAMPLED_HASH_HEAD_BYTES,
  SAMPLED_HASH_TAIL_BYTES,
  HASH_READ_CHUNK_BYTES,
  MAX_FILE_BYTES as SHARED_MAX_FILE_BYTES,
  workspaceRoom,
  boardRoom,
  assetRoom,
  parseRoom,
  WS_PRESENCE_HEARTBEAT_MS,
  WS_EVENT_TYPES,
  WS_RESERVED_EVENT_TYPES,
  type WsEventType,
} from '@lumen/shared';

// --- release3/A --- notifications (REV-1) + mentions: the shared token helpers and preference defaults
export { MENTION_TOKEN_RE, NOTIFICATION_PREFERENCE_DEFAULTS, mentionToken, parseMentionTokens } from '@lumen/shared';
export { NotificationChannel as NotificationChannelSchema, NotificationKind as NotificationKindSchema } from '@lumen/shared';
